import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, FIELD, PageHeader } from "../components/account-ui.tsx";
import { Button, HighlightedButton } from "../components/legacy/ui.tsx";
import { CheckIcon, XMarkIcon } from "../icons/index.tsx";
import { api } from "../lib/api.ts";
import { useAddresses } from "./account.address-book.tsx";

export const Route = createFileRoute("/account/address-book/$id")({ component: EditAddress });

const EMPTY = {
  fullName: "",
  company: "",
  locationName: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  province: "",
  postalCode: "",
  countryCode: "AU",
  phone: "",
  defaultShipping: false,
  defaultBilling: false,
};

/** The Vendure AddressForm: two-column grid of labelled inputs plus defaults. */
function EditAddress() {
  const { id } = Route.useParams();
  const isNew = id === "add";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useAddresses();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    const a = data?.addresses.find((x) => x.id === id);
    if (!a) return;
    setForm({
      fullName: a.full_name ?? "",
      company: a.company ?? "",
      locationName: a.location_name ?? "",
      streetLine1: a.street_line1,
      streetLine2: a.street_line2 ?? "",
      city: a.city,
      province: a.province ?? "",
      postalCode: a.postal_code,
      countryCode: a.country_code,
      phone: a.phone ?? "",
      defaultShipping: a.default_shipping === 1,
      defaultBilling: a.default_billing === 1,
    });
  }, [data, id]);

  const save = useMutation({
    mutationFn: () =>
      isNew ? api.post("/account/addresses", form) : api.patch(`/account/addresses/${id}`, form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      await queryClient.invalidateQueries({ queryKey: ["config"] });
      navigate({ to: "/account/address-book" });
    },
  });

  const set =
    (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({
        ...form,
        [k]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value,
      });

  const text = (
    label: string,
    k: keyof typeof EMPTY,
    span = false,
    autoComplete?: string,
    placeholder?: string,
  ) => (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={k}>
        {label}
      </label>
      <input
        id={k}
        type="text"
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={form[k] as string}
        onChange={set(k)}
        className={FIELD}
      />
    </div>
  );

  return (
    <div>
      <PageHeader
        title={isNew ? "New location" : "Edit location"}
        subtitle="An organisation location. It can also be your billing address."
      />
      <Card className="max-w-2xl">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {text("Full name", "fullName", true, "name")}
          {text("Company", "company", false, "organization")}
          {text(
            "Location name",
            "locationName",
            false,
            undefined,
            "e.g. Head Office, Shopping Town…",
          )}
          {text("Address", "streetLine1", true, "address-line1")}
          {text("Apartment, suite, etc.", "streetLine2", true, "address-line2")}
          {text("City", "city", false, "address-level2")}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="countryCode">
              Country
            </label>
            <select
              id="countryCode"
              value={form.countryCode}
              onChange={set("countryCode")}
              className={FIELD}
            >
              <option value="AU">Australia</option>
              <option value="NZ">New Zealand</option>
            </select>
          </div>
          {text("State / Province", "province", false, "address-level1")}
          {text("Postal code", "postalCode", false, "postal-code")}
          {text("Phone", "phone", true, "tel")}
          <div className="flex flex-wrap gap-x-8 gap-y-3 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.defaultShipping}
                onChange={set("defaultShipping")}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Default location
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.defaultBilling}
                onChange={set("defaultBilling")}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Default billing address
            </label>
          </div>
        </div>
        <div className="mt-8 flex gap-x-4">
          <HighlightedButton
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.streetLine1 || !form.city || !form.postalCode}
          >
            <CheckIcon /> &nbsp; Save
          </HighlightedButton>
          <Button onClick={() => navigate({ to: "/account/address-book" })}>
            <XMarkIcon forcedClass="w-4 h-4" /> &nbsp; Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}
